import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiBase } from '../config/config';

export const useApi = () => {
  const { token, refreshToken, logout } = useAuth();

  const apiCall = useCallback(async (endpoint, options = {}) => {
    if (!token) {
      throw new Error('No authentication token available');
    }

    let currentToken = token;

    const makeRequest = async (authToken) => {
      // For FormData bodies, let the browser set Content-Type (with boundary).
      const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
      const baseHeaders = {
        'Authorization': `Bearer ${authToken}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      };

      return await fetch(`${getApiBase()}${endpoint}`, {
        ...options,
        headers: {
          ...baseHeaders,
          ...options.headers,
        },
      });
    };

    let response = await makeRequest(currentToken);

    // If unauthorized, try refreshing the token
    if (response.status === 401) {
      currentToken = await refreshToken();
      
      if (currentToken) {
        response = await makeRequest(currentToken);
      } else {
        // Refresh failed, logout user
        await logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `Request failed with status ${response.status}`);
    }

    return response.json();
  }, [token, refreshToken, logout]);

  return { apiCall };
};

export default useApi;